import AddIcon from "@mui/icons-material/Add";
import { IconButton, Typography } from "@mui/material";
import {
  MaterialReactTable,
  MRT_ColumnDef,
  MRT_Row,
  useMaterialReactTable,
} from "material-react-table";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import getAxiosError from "../../../app/api/axiosError";
import { updateUserTokenToInvalid } from "../../../app/features/login/loginSlice";
import { getparcelCenterList } from "../../../app/features/parcelCenter/parcelCenterSlice";
import { RootState, useAppDispatch } from "../../../app/features/store/store";
import { ClientParcelCenterFdp } from "../../../app/models/responseModels/clientsResponse";
import {
  useSubmitModal,
  useSubmitModalWithParameter,
} from "../../../components/common/modalPopup/useSubmitModal";
import { CustomTooltip } from "../../../components/common/tooltip/CustomToolTip";
import { PAGES } from "../../../enums/PagesEnum";
import { getDefaultMRTOptions } from "../../dedicatedDelivery/DefaultMrtOptions";
import ParcelCenterMappingModalV1, {
  InitialValues,
} from "../forms/AddParcelCenterMappingModal";
import { getfdpList } from "../../../app/features/fdp/fdpSlice";
import AddParcelCenterMappingModal from "../forms/AddParcelCenterMappingModal";
import EditParcelCenterMappingModal from "../forms/EditParcelCenterMappingModal";
import { ParcelCenterResponse } from "../../../app/models/responseModels/parcelCenterResponse";
import { FdpResponse } from "../../../app/models/responseModels/FdpResponse";
import Loader from "../../../components/common/loader/Loader";

type AssociatedParcelCenterGridProp = {
  data: ClientParcelCenterFdp[];
  clientId: number;
};

export type TransformedData = {
  clientId: number;
  parcelCenter: ParcelCenterResponse;
  fdps: FdpResponse[];
};

const AssociatedParcelCenterGrid: React.FC<AssociatedParcelCenterGridProp> = ({
  data,
  clientId,
}) => {
  const defaultMRTOptions = getDefaultMRTOptions<TransformedData>();

  const { parcelCenterList } = useSelector(
    (state: RootState) => state.parcelCenter
  );

  const { fdpList } = useSelector((state: RootState) => state.fdp);

  const [transformedData, setTransformedData] = useState<TransformedData[]>([]);

  const transformData = (
    data: ClientParcelCenterFdp[],
    parcelCenterList: ParcelCenterResponse[],
    fdpList: FdpResponse[]
  ): TransformedData[] => {
    const result: { [key: number]: { clientId: number; fdpIds: number[] } } =
      {};

    data.forEach((item) => {
      if (!result[item.parcelCenterId]) {
        result[item.parcelCenterId] = {
          clientId: item.clientId,
          fdpIds: [],
        };
      }
      if (!result[item.parcelCenterId].fdpIds.includes(item.fdpId)) {
        result[item.parcelCenterId].fdpIds.push(item.fdpId);
      }
    });

    return Object.keys(result).map((parcelCenterId) => {
      const parcelCenter = parcelCenterList.find(
        (pc) => parseInt(pc.parcelCenterId) == parseInt(parcelCenterId, 10)
      );
      const fdps = result[parseInt(parcelCenterId)].fdpIds.map((fdpId) =>
        fdpList.find((fdp) => fdp.id == fdpId)
      ) as FdpResponse[];

      return {
        clientId: clientId,
        parcelCenter: parcelCenter!,
        fdps,
      };
    });
  };

  useEffect(() => {
    const res = transformData(data, parcelCenterList, fdpList);
    setTransformedData(res);
  }, [data, parcelCenterList, fdpList]);

  // ***** Get page Permissions *****
  const pagePermission = useSelector((state: RootState) =>
    state.login?.authResponse?.userSettings?.pagePermissions.find(
      (x) => x.pageName === PAGES.PARCEL_CENTERS
    )
  );
  const IsCreatePermission = pagePermission
    ? pagePermission.isCreatePermission
    : false;

  const IsAdmin = useSelector((state: RootState) =>
    state.login?.authResponse?.userSettings
      ? state.login?.authResponse?.userSettings?.roles.includes(
          "Administrators"
        )
      : false
  );

  // Columns to be used on grid on landing page
  const columns = useMemo<MRT_ColumnDef<TransformedData>[]>(
    () => [
      {
        header: "Name",
        Cell: ({ cell, row }) => row.original.parcelCenter.parcelCenterName,
      },
      {
        header: "Address",
        Cell: ({ cell, row }) => row.original.parcelCenter.address,
      },
      {
        header: "City",
        Cell: ({ cell, row }) => row.original.parcelCenter.city,
      },
      {
        header: "State",
        Cell: ({ cell, row }) => row.original.parcelCenter.state,
      },
      {
        header: "ZipCode",
        Cell: ({ cell, row }) => row.original.parcelCenter.zipCode,
      },
      {
        header: "Associated Fdp(s)",
        Cell: ({ cell, row }) =>
          row.original.fdps.map((e) => e.companyName).join(", "),
        size: 100,
      },
    ],
    [parcelCenterList, fdpList]
  );

  const handleAfterNewMapping = (req: InitialValues) => {
    const result: TransformedData[] = req.clientParcelCenterFdpMappings.map(
      (e) => {
        const fdps = fdpList.filter((f) => e.fdpIds.includes(f.id));
        const parcelCenter = parcelCenterList.filter(
          (f) => f.parcelCenterId === e.parcelCenterId
        )[0];
        return {
          clientId: req.clientId,
          fdps: fdps,
          parcelCenter: parcelCenter,
        };
      }
    );
    setTransformedData((prevData) => {
      const updatedData = [...prevData];

      result.forEach((newData) => {
        const index = updatedData.findIndex(
          (data) =>
            data.parcelCenter.parcelCenterId ===
            newData.parcelCenter.parcelCenterId
        );

        if (index !== -1) {
          // Update the existing entry
          updatedData[index] = newData;
        } else {
          // Add the new entry
          updatedData.push(newData);
        }
      });

      return updatedData;
    });
  };

  const [parcelCenterMappingModal, openParcelCenterMappingModal] =
    useSubmitModal((closeFdpMappingModal) => (
      <AddParcelCenterMappingModal
        clientId={clientId}
        data={transformedData}
        alreadyMappedPcs={data.map((e) => e.parcelCenterId)}
        closeModal={closeFdpMappingModal}
        onCompleted={handleAfterNewMapping}
      />
    ));

  const [editParcelCenterMappingModal, openEditParcelCenterMappingModal] =
    useSubmitModalWithParameter<TransformedData>(
      (rowData, closeFdpMappingModal) => (
        <EditParcelCenterMappingModal
          clientId={clientId}
          rowData={rowData}
          closeModal={closeFdpMappingModal}
          onCompleted={handleAfterNewMapping}
        />
      )
    );

  // preprare MRT V2 grid
  const table = useMaterialReactTable({
    ...defaultMRTOptions,
    columns: columns,
    data: transformedData,
    initialState: {
      ...defaultMRTOptions.initialState,
      columnVisibility: {
        id: true,
      },
      sorting: [{ desc: true, id: "id" }],
    },
    state: {
      showProgressBars: false,
      sorting: [{ desc: true, id: "id" }],
    },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: (e) => {
        openEditParcelCenterMappingModal(row.original);
      },
      ...defaultMRTOptions.muiTableBodyRowProps,
      sx: {
        cursor: "pointer",
      },
    }),
    renderTopToolbarCustomActions: ({ table }) =>
      IsCreatePermission &&
      IsAdmin && (
        <CustomTooltip
          arrow
          title="Map Client to Parcel Center"
          onClick={() => {
            openParcelCenterMappingModal();
          }}
        >
          <IconButton type="button">
            <AddIcon sx={{ color: "primary.main" }} />
          </IconButton>
        </CustomTooltip>
      ),
    renderBottomToolbarCustomActions: () => {
      return (
        <Typography variant="subtitle2" component="p">
          Total Rows: {data.length}
        </Typography>
      );
    },
  });
  return (
    <>
      <MaterialReactTable table={table} />
      {parcelCenterMappingModal}
      {editParcelCenterMappingModal}
    </>
  );
};

export default AssociatedParcelCenterGrid;
